// Types et constantes de l'auth sans dépendance serveur, importables côté
// client (même rôle que lib/leads-shared.ts).
import type { Locale } from "@shared/i18n/locales";

/** Profil porté par le compte (équivalent de `raw_user_meta_data` chez Supabase). */
export type UserMetadata = {
  full_name?: string;
  first_name?: string;
  last_name?: string;
  /** Réponse à « Comment avez-vous connu Hune ? » (onboarding, facultative). */
  acquisition_source?: string;
  company?: string;
  role?: string;
  website?: string;
  onboarding_completed?: boolean;
  /** Posé à la fin de l'onboarding, retiré quand la visite guidée se termine ou est passée. */
  product_tour_pending?: boolean;
  [key: string]: unknown;
};

/** Drapeaux de profil que l'utilisateur pose lui-même (onboarding, visite guidée). */
export const BOOLEAN_METADATA = ["onboarding_completed", "product_tour_pending"] as const;

export type User = {
  id: string;
  email: string;
  user_metadata: UserMetadata;
  /** Interface language of the account (app, emails, agent output). */
  locale: Locale;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
};

export const SESSION_COOKIE = "hune-session";
export const MIN_PASSWORD_LENGTH = 8;

/** Champs de profil modifiables par l'utilisateur lui-même, avec leur longueur maximale. */
export const EDITABLE_METADATA: Record<string, number> = {
  full_name: 160,
  first_name: 80,
  last_name: 80,
  acquisition_source: 60,
  company: 160,
  role: 160,
  website: 300,
};

/**
 * Prénom et nom du profil : ceux saisis, sinon le nom complet coupé au premier
 * espace (comptes antérieurs à la séparation, comme dans l'admin).
 */
export function nameParts(metadata: UserMetadata | null | undefined) {
  const full = typeof metadata?.full_name === "string" ? metadata.full_name.trim() : "";
  const [head = "", ...rest] = full.split(/\s+/).filter(Boolean);
  const first = typeof metadata?.first_name === "string" && metadata.first_name.trim() ? metadata.first_name.trim() : head;
  const last = typeof metadata?.last_name === "string" && metadata.last_name.trim() ? metadata.last_name.trim() : rest.join(" ");
  return { first, last };
}

/** Le nom complet recomposé, lu partout ailleurs (topbar, admin, e-mails). */
export const joinName = (first: string, last: string) => [first.trim(), last.trim()].filter(Boolean).join(" ").slice(0, 160);

/** Destination interne sûre après connexion (jamais une autre origine). */
export function safeNext(value: string | null | undefined, fallback = "/app") {
  return value?.startsWith("/") && !value.startsWith("//") && !value.startsWith("/\\") ? value : fallback;
}

/** Réponse de GET /api/session (gardes des fronts). */
export type SessionInfo = {
  user: { id: string; email: string; user_metadata: UserMetadata; locale: Locale };
  onboarded: boolean;
  isAdmin: boolean;
};
